import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { sdk } from "@/lib/medusa";

import {
  getCartIdCookie,
  removeCartIdCookie,
  setCartIdCookie,
} from "@/lib/cartCookies";

import { isPaidCartLocked, unlockCompletedCart } from "@/lib/cartPaymentLock";

import { ORDER_TYPE, normalizeOrderType } from "@/lib/orderType";

/* =========================================================
   EMPTY CART BUCKET
   ========================================================= */

const EMPTY_BUCKET = {
  cart: null,
  loading: false,
  error: "",
};

/* =========================================================
   CART CREATION PROMISES

   Prevent multiple carts from being created at the same time.
   ========================================================= */

const cartCreationPromises = {
  [ORDER_TYPE.GROCERY]: null,
  [ORDER_TYPE.TAKEOUT]: null,
};

/* =========================================================
   RUNTIME CART CACHE
   ========================================================= */

const runtimeCartCache = {
  [ORDER_TYPE.GROCERY]: null,
  [ORDER_TYPE.TAKEOUT]: null,
};

/* =========================================================
   INITIAL STATE
   ========================================================= */

const initialState = {
  initialized: false,
  initializing: false,
  region: null,
  error: "",

  grocery: {
    ...EMPTY_BUCKET,
  },

  takeout: {
    ...EMPTY_BUCKET,
  },
};

/* =========================================================
   ORDER TYPE
   ========================================================= */

function bucketKey(orderType) {
  return normalizeOrderType(orderType) || ORDER_TYPE.GROCERY;
}

/* =========================================================
   PAYMENT FIELDS

   We need the payment collection/session when retrieving
   a cart so we can identify already-authorized carts.
   ========================================================= */

function paymentFields() {
  return "+payment_collection,+payment_collection.payment_sessions";
}

/* =========================================================
   PAYMENT SESSION LOCK CHECK

   Medusa payment sessions can become authorized after the
   provider successfully authorizes payment.

   Stripe can also automatically capture payment, so we
   protect "captured" as well.

   pending_authorization is also treated as locked because
   that payment is already in its authorization flow.
   ========================================================= */

function hasFinalizedPaymentSession(cart) {
  const sessions = cart?.payment_collection?.payment_sessions || [];

  return sessions.some((session) => {
    const status = String(session?.status || "")
      .trim()
      .toLowerCase();

    return (
      status === "authorized" ||
      status === "captured" ||
      status === "pending_authorization"
    );
  });
}

/* =========================================================
   IS CART LOCKED?
   ========================================================= */

function isCartPaymentLocked(cart) {
  if (!cart?.id) {
    return false;
  }

  /*
   * Two protections:
   *
   * 1. Browser payment lock written immediately when
   *    Stripe returns "succeeded".
   *
   * 2. Medusa's payment-session status.
   */

  return isPaidCartLocked(cart.id) || hasFinalizedPaymentSession(cart);
}

/* =========================================================
   ASSERT CART IS EDITABLE

   EVERY cart mutation should run this first.
   ========================================================= */

function assertCartEditable(cart) {
  if (!cart?.id) {
    throw new Error("Cart is not ready.");
  }

  if (isCartPaymentLocked(cart)) {
    throw new Error(
      "This basket has already been paid and can no longer be edited.",
    );
  }
}

/* =========================================================
   REMEMBER CART

   Keep:
   - cookie
   - runtime cache

   synchronized.
   ========================================================= */

function rememberCart(orderType, cart) {
  if (!cart?.id) {
    return;
  }

  runtimeCartCache[orderType] = cart;

  setCartIdCookie(orderType, cart.id);
}

/* =========================================================
   CANADA REGION
   ========================================================= */

async function getCanadaRegion() {
  const { regions } = await sdk.store.region.list({
    fields: "*countries",
  });

  const canadaRegion = regions?.find((region) =>
    region.countries?.some((country) => country.iso_2?.toLowerCase() === "ca"),
  );

  if (!canadaRegion) {
    throw new Error("Canada region is not configured in Medusa.");
  }

  return canadaRegion;
}

/* =========================================================
   RESTORE CART FROM COOKIE
   ========================================================= */

async function restoreCart(orderType, region) {
  const savedId = getCartIdCookie(orderType);

  if (!savedId) {
    return null;
  }

  try {
    /* ===============================================
       RETRIEVE CART
       =============================================== */

    let { cart } = await sdk.store.cart.retrieve(savedId, {
      fields: paymentFields(),
    });

    /* ===============================================
       REGION CHANGED

       Never update region on an already-paid cart.

       Updating region changes the cart and can cause
       Medusa to refresh/delete its payment session.
       =============================================== */

    if (cart.region_id !== region.id) {
      if (isCartPaymentLocked(cart)) {
        console.warn(
          `${orderType} cart ${cart.id} has already entered payment and cannot change region.`,
        );
      } else {
        const response = await sdk.store.cart.update(cart.id, {
          region_id: region.id,
        });

        cart = response.cart;
      }
    }

    /* ===============================================
       REMEMBER CART
       =============================================== */

    rememberCart(orderType, cart);

    return cart;
  } catch (error) {
    console.warn(`Unable to restore ${orderType} cart:`, error);

    removeCartIdCookie(orderType);

    runtimeCartCache[orderType] = null;

    return null;
  }
}

/* =========================================================
   CREATE CART
   ========================================================= */

async function createCart(orderType, region) {
  if (!region?.id) {
    throw new Error("Canada region is not ready.");
  }

  /* ===============================================
     EXISTING CREATION IN PROGRESS
     =============================================== */

  if (cartCreationPromises[orderType]) {
    return cartCreationPromises[orderType];
  }

  /* ===============================================
     CREATE
     =============================================== */

  cartCreationPromises[orderType] = (async () => {
    const { cart } = await sdk.store.cart.create({
      region_id: region.id,

      metadata: {
        order_type: orderType,
      },
    });

    rememberCart(orderType, cart);

    return cart;
  })();

  try {
    return await cartCreationPromises[orderType];
  } finally {
    cartCreationPromises[orderType] = null;
  }
}

/* =========================================================
   GET OR CREATE CART
   ========================================================= */

async function getOrCreateCart(orderType, state) {
  const key = bucketKey(orderType);

  /* ===============================================
     REDUX CART
     =============================================== */

  const existing = state.cart[key]?.cart;

  if (existing?.id) {
    runtimeCartCache[key] = existing;

    return existing;
  }

  /* ===============================================
     RUNTIME CACHE
     =============================================== */

  if (runtimeCartCache[key]?.id) {
    return runtimeCartCache[key];
  }

  /* ===============================================
     CREATE NEW CART
     =============================================== */

  const region = state.cart.region || (await getCanadaRegion());

  return createCart(key, region);
}

/* =========================================================
   INITIALIZE BOTH CARTS
   ========================================================= */

export const initializeCarts = createAsyncThunk(
  "cart/initialize",

  async (_, { rejectWithValue }) => {
    try {
      const region = await getCanadaRegion();

      const [groceryCart, takeoutCart] = await Promise.all([
        restoreCart(ORDER_TYPE.GROCERY, region),

        restoreCart(ORDER_TYPE.TAKEOUT, region),
      ]);

      return {
        region,
        groceryCart,
        takeoutCart,
      };
    } catch (error) {
      return rejectWithValue(error?.message || "Unable to initialize carts.");
    }
  },
);

/* =========================================================
   ADD ITEM
   ========================================================= */

export const addCartItem = createAsyncThunk(
  "cart/addItem",

  async (
    { orderType, variantId, quantity = 1, metadata },

    { getState, rejectWithValue },
  ) => {
    const type = bucketKey(orderType);

    try {
      /* =========================================
           VALIDATE VARIANT
           ========================================= */

      if (!variantId) {
        throw new Error("Product variant is missing.");
      }

      /* =========================================
           GET CART
           ========================================= */

      const activeCart = await getOrCreateCart(type, getState());

      /* =========================================
           IMPORTANT

           Never add items to an already-paid cart.
           ========================================= */

      assertCartEditable(activeCart);

      /* =========================================
           LINE ITEM DATA
           ========================================= */

      const lineItemData = {
        variant_id: variantId,

        quantity,
      };

      if (metadata && Object.keys(metadata).length) {
        lineItemData.metadata = metadata;
      }

      /* =========================================
           CREATE ITEM
           ========================================= */

      const { cart } = await sdk.store.cart.createLineItem(
        activeCart.id,
        lineItemData,
      );

      rememberCart(type, cart);

      return {
        orderType: type,

        cart,
      };
    } catch (error) {
      return rejectWithValue({
        orderType: type,

        message:
          error?.message || "Unable to add this item. It may be out of stock.",
      });
    }
  },
);

/* =========================================================
   UPDATE ITEM QUANTITY
   ========================================================= */

export const updateCartItemQuantity = createAsyncThunk(
  "cart/updateItemQuantity",

  async (
    { orderType, itemId, quantity, metadata },

    { getState, rejectWithValue },
  ) => {
    const type = bucketKey(orderType);

    try {
      /* =========================================
           GET CART
           ========================================= */

      const cart = getState().cart[type]?.cart;

      if (!cart?.id) {
        throw new Error("Cart is not ready.");
      }

      /* =========================================
           PAYMENT PROTECTION
           ========================================= */

      assertCartEditable(cart);

      /* =========================================
           QUANTITY 0 = DELETE ITEM
           ========================================= */

      if (quantity <= 0) {
        const { parent } = await sdk.store.cart.deleteLineItem(cart.id, itemId);

        rememberCart(type, parent);

        return {
          orderType: type,

          cart: parent,
        };
      }

      /* =========================================
           UPDATE DATA
           ========================================= */

      const lineItemData = {
        quantity,
      };

      if (metadata && Object.keys(metadata).length) {
        lineItemData.metadata = metadata;
      }

      /* =========================================
           UPDATE ITEM
           ========================================= */

      const { cart: updated } = await sdk.store.cart.updateLineItem(
        cart.id,
        itemId,
        lineItemData,
      );

      rememberCart(type, updated);

      return {
        orderType: type,

        cart: updated,
      };
    } catch (error) {
      return rejectWithValue({
        orderType: type,

        message:
          error?.message || "Unable to update quantity. Check available stock.",
      });
    }
  },
);

/* =========================================================
   REMOVE CART ITEM
   ========================================================= */

export const removeCartItem = createAsyncThunk(
  "cart/removeItem",

  async (
    { orderType, itemId },

    { getState, rejectWithValue },
  ) => {
    const type = bucketKey(orderType);

    try {
      /* =========================================
           GET CART
           ========================================= */

      const cart = getState().cart[type]?.cart;

      if (!cart?.id) {
        throw new Error("Cart is not ready.");
      }

      /* =========================================
           PAYMENT PROTECTION
           ========================================= */

      assertCartEditable(cart);

      /* =========================================
           REMOVE ITEM
           ========================================= */

      const { parent } = await sdk.store.cart.deleteLineItem(cart.id, itemId);

      rememberCart(type, parent);

      return {
        orderType: type,

        cart: parent,
      };
    } catch (error) {
      return rejectWithValue({
        orderType: type,

        message: error?.message || "Unable to remove item.",
      });
    }
  },
);

/* =========================================================
   REFRESH CART

   READ ONLY.

   This is intentionally NOT blocked for paid carts because
   we still need to retrieve an already-paid cart when
   finalizing an order.
   ========================================================= */

export const refreshCart = createAsyncThunk(
  "cart/refresh",

  async (
    { orderType, cartId },

    { getState, rejectWithValue },
  ) => {
    const type = bucketKey(orderType);

    try {
      /* =========================================
           CART ID
           ========================================= */

      const id =
        cartId || getState().cart[type]?.cart?.id || getCartIdCookie(type);

      if (!id) {
        return {
          orderType: type,

          cart: null,
        };
      }

      /* =========================================
           RETRIEVE
           ========================================= */

      const { cart } = await sdk.store.cart.retrieve(id, {
        fields: paymentFields(),
      });

      /* =========================================
           CACHE
           ========================================= */

      rememberCart(type, cart);

      return {
        orderType: type,

        cart,
      };
    } catch (error) {
      /* =========================================
           CART NO LONGER EXISTS / COMPLETED
           ========================================= */

      removeCartIdCookie(type);

      runtimeCartCache[type] = null;

      return rejectWithValue({
        orderType: type,

        message: error?.message || "Unable to refresh cart.",
      });
    }
  },
);

/* =========================================================
   UPDATE CART DETAILS

   Used for:
   - email
   - shipping address
   - billing address
   - metadata
   - etc.

   THIS WAS THE POST /store/carts/:id CAUSING YOUR ERROR.
   ========================================================= */

export const updateCartDetails = createAsyncThunk(
  "cart/updateDetails",

  async (
    { orderType, data },

    { getState, rejectWithValue },
  ) => {
    const type = bucketKey(orderType);

    try {
      /* =========================================
           GET CART
           ========================================= */

      const cart = await getOrCreateCart(type, getState());

      /* =========================================
           VERY IMPORTANT

           If Stripe already succeeded, STOP HERE.

           Do not send:
           POST /store/carts/:id
           ========================================= */

      assertCartEditable(cart);

      /* =========================================
           UPDATE
           ========================================= */

      const { cart: updated } = await sdk.store.cart.update(cart.id, data);

      rememberCart(type, updated);

      return {
        orderType: type,

        cart: updated,
      };
    } catch (error) {
      return rejectWithValue({
        orderType: type,

        message: error?.message || "Unable to update cart details.",
      });
    }
  },
);

/* =========================================================
   ADD SHIPPING METHOD
   ========================================================= */

export const addCartShippingMethod = createAsyncThunk(
  "cart/addShippingMethod",

  async (
    { orderType, optionId },

    { getState, rejectWithValue },
  ) => {
    const type = bucketKey(orderType);

    try {
      /* =========================================
           GET CART
           ========================================= */

      const cart = getState().cart[type]?.cart;

      if (!cart?.id) {
        throw new Error("Cart is not ready.");
      }

      /* =========================================
           PAYMENT PROTECTION
           ========================================= */

      assertCartEditable(cart);

      /* =========================================
           ADD SHIPPING METHOD
           ========================================= */

      const { cart: updated } = await sdk.store.cart.addShippingMethod(
        cart.id,
        {
          option_id: optionId,

          data: {},
        },
      );

      rememberCart(type, updated);

      return {
        orderType: type,

        cart: updated,
      };
    } catch (error) {
      return rejectWithValue({
        orderType: type,

        message: error?.message || "Unable to add shipping method.",
      });
    }
  },
);

/* =========================================================
   CLEAR COMPLETED CART

   Called ONLY after:
   sdk.store.cart.complete(...)
   successfully returned an order.
   ========================================================= */

export const clearCompletedCart = createAsyncThunk(
  "cart/clearCompleted",

  async (
    { orderType },

    { getState },
  ) => {
    const type = bucketKey(orderType);

    /* =========================================
         SAVE OLD CART ID BEFORE CLEARING
         ========================================= */

    const cartId =
      getState().cart[type]?.cart?.id ||
      runtimeCartCache[type]?.id ||
      getCartIdCookie(type);

    /* =========================================
         ORDER SUCCESSFULLY EXISTS

         Payment lock can now be removed.
         ========================================= */

    if (cartId) {
      unlockCompletedCart(cartId);
    }

    /* =========================================
         REMOVE OLD CART COOKIE
         ========================================= */

    removeCartIdCookie(type);

    /* =========================================
         REMOVE RUNTIME CART
         ========================================= */

    runtimeCartCache[type] = null;

    return {
      orderType: type,
    };
  },
);

/* =========================================================
   RESET CARTS AFTER LOGOUT
   ========================================================= */

export const resetCartsAfterLogout = createAsyncThunk(
  "cart/resetAfterLogout",

  async () => {
    [ORDER_TYPE.GROCERY, ORDER_TYPE.TAKEOUT].forEach((type) => {
      /* =========================================
           REMOVE CART REFERENCE

           We intentionally do NOT remove payment locks
           here. If Stripe already succeeded but order
           finalization hasn't completed, the old cart
           must remain protected.
           ========================================= */

      removeCartIdCookie(type);

      runtimeCartCache[type] = null;
    });

    return true;
  },
);

/* =========================================================
   SLICE
   ========================================================= */

const cartSlice = createSlice({
  name: "cart",

  initialState,

  reducers: {
    /* =========================================
         CLEAR ERROR
         ========================================= */

    clearCartError(state, action) {
      const type = bucketKey(action.payload);

      state[type].error = "";
    },
  },

  extraReducers: (builder) => {
    /* =========================================
         INITIALIZE
         ========================================= */

    builder
      .addCase(
        initializeCarts.pending,

        (state) => {
          state.initializing = true;

          state.error = "";
        },
      )

      .addCase(
        initializeCarts.fulfilled,

        (state, action) => {
          state.initializing = false;

          state.initialized = true;

          state.region = action.payload.region;

          state.grocery.cart = action.payload.groceryCart;

          state.takeout.cart = action.payload.takeoutCart;
        },
      )

      .addCase(
        initializeCarts.rejected,

        (state, action) => {
          state.initializing = false;

          state.initialized = true;

          state.error =
            action.payload ||
            action.error.message ||
            "Unable to initialize carts.";
        },
      );

    /* =========================================
         STANDARD CART REQUESTS
         ========================================= */

    const pendingHandlers = [
      addCartItem,

      updateCartItemQuantity,

      removeCartItem,

      refreshCart,

      updateCartDetails,

      addCartShippingMethod,
    ];

    pendingHandlers.forEach((thunk) => {
      /* =====================================
             PENDING
             ===================================== */

      builder.addCase(
        thunk.pending,

        (state, action) => {
          const type = bucketKey(action.meta.arg?.orderType);

          state[type].loading = true;

          state[type].error = "";
        },
      );

      /* =====================================
             FULFILLED
             ===================================== */

      builder.addCase(
        thunk.fulfilled,

        (state, action) => {
          const type = bucketKey(
            action.payload?.orderType || action.meta.arg?.orderType,
          );

          state[type].loading = false;

          state[type].error = "";

          state[type].cart = action.payload?.cart ?? state[type].cart;
        },
      );

      /* =====================================
             REJECTED
             ===================================== */

      builder.addCase(
        thunk.rejected,

        (state, action) => {
          const type = bucketKey(
            action.payload?.orderType || action.meta.arg?.orderType,
          );

          state[type].loading = false;

          state[type].error =
            action.payload?.message ||
            action.error.message ||
            "Cart request failed.";

          /*
           * Only refresh failure clears Redux cart.
           */

          if (thunk === refreshCart) {
            state[type].cart = null;
          }
        },
      );
    });

    /* =========================================
         CLEAR COMPLETED CART
         ========================================= */

    builder.addCase(
      clearCompletedCart.fulfilled,

      (state, action) => {
        const type = bucketKey(action.payload.orderType);

        state[type] = {
          ...EMPTY_BUCKET,
        };
      },
    );

    /* =========================================
         LOGOUT
         ========================================= */

    builder.addCase(
      resetCartsAfterLogout.fulfilled,

      (state) => {
        state.grocery = {
          ...EMPTY_BUCKET,
        };

        state.takeout = {
          ...EMPTY_BUCKET,
        };

        state.error = "";
      },
    );
  },
});

/* =========================================================
   EXPORT
   ========================================================= */

export const { clearCartError } = cartSlice.actions;

export default cartSlice.reducer;
